const BASE = "/api/v1";

/** Encode each segment but keep the slashes — folder paths may nest. */
function encodePath(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem("rdp_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type JobStatus =
  | "QUEUED" | "RUNNING" | "INTAKE_COMPLETE" | "CATEGORIZED"
  | "NEEDS_HUMAN_APPROVAL" | "RESUMING" | "COMPLETED" | "FAILED"
  | "AI_INTAKE_PAUSED";

export interface JobPageOut {
  id: string;
  job_id: string;
  page_num: number;
  is_chart: boolean;
  thumbnail_s3_key: string | null;
  chart_category: string | null;
  session_id: string | null;
  intake_pdf_key: string | null;
}

export type ChartStatus =
  | "PENDING" | "PROCESSING" | "AUTO_APPROVED"
  | "NEEDS_REVIEW" | "APPROVED" | "REJECTED" | "COMPLETED" | "FAILED";

export interface JobListItem {
  id: string;
  source_filename: string;
  status: JobStatus;
  pending_hitl_count: number;
  chart_count: number;
  assigned_operator: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ChartOut {
  id: string;
  job_id: string;
  page_num: number;
  chart_idx: number;
  status: ChartStatus;
  image_s3_key: string | null;
  thumbnail_s3_key: string | null;
  image_width_px: number | null;
  image_height_px: number | null;
  pipeline_confidence: number | null;
  gate_failure: Record<string, unknown> | null;
  zone_path: string | null;
  total_latency_ms: number | null;
  triage_result: Record<string, unknown> | null;
  detection_result: Record<string, unknown> | null;
  ocr_result: Record<string, unknown> | null;
  calibration_result: Record<string, unknown> | null;
  extraction_result: Record<string, unknown> | null;
  chart_category: string | null;
  session_id: string | null;
  intake_pdf_key: string | null;
  has_hitl_session: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface JobOut extends JobListItem {
  source_s3_key: string;
  charts: ChartOut[];
  completed_at: string | null;
}

export interface HITLInitResponse {
  hitl_session_id: string;
  wpd_editor_url: string;
  image_url: string;
  pipeline_confidence: number | null;
  trigger_reason: string;
  trigger_details: Record<string, unknown> | null;
  chart_type: string | null;
  page_num: number;
  chart_idx: number;
}

export interface UsageSummary {
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  call_count: number;
}

export interface ModelConfig {
  step_name: string;
  provider: string | null;
  model_name: string | null;
  api_key_set: boolean;
  endpoint_url: string | null;
  is_active: boolean;
  usage: UsageSummary | null;
}

export interface ModelConfigIn {
  provider?: string;
  model_name?: string;
  api_key?: string;
  endpoint_url?: string;
  is_active?: boolean;
}

export interface BatchFolder {
  name: string;
  path: string;
  folder_count?: number;
  job_count?: number;
}

export interface BatchPanel {
  name: string;
  size: number;
  url: string;
  page_num: number | null;
  bbox: [number, number, number, number] | null;
  page_w: number | null;
  page_h: number | null;
  // "Work done" flags shown as badges in the folder view.
  has_ocr?: boolean;      // ran OCR at least once
  has_trace?: boolean;    // trace worker produced a result
  corrected?: boolean;    // operator manually confirmed something (axes or bbox)
}

export type BatchJobStatus = "queued" | "processing" | "completed" | "failed";

export interface BatchJob {
  job_id: string;
  filename: string;
  created_at: string | null;
  pages_processed: number;
  total_panels: number;
  detector: string;
  status: BatchJobStatus;
  error: string | null;
  extracted_to: string | null;
  source_url: string | null;
  panels: BatchPanel[];
}

export interface BatchContents {
  path: string;
  folders: BatchFolder[];
  jobs: BatchJob[];
  images: BatchPanel[];
}

/** Returned as soon as the PDF is stored and the crop task is enqueued. */
export interface BatchCropResult {
  path: string;
  job_id: string;
  task_id: string;
  filename: string;
  created_at: string;
  status: BatchJobStatus;
  total_pages: number;
}

export interface HITLError {
  session_id: string;
  chart_id: string;
  stage: string;
  reason: string;
  confidence: number | null;
  created_at: string | null;
  outcome: string | null;
}

export interface ApproveResult {
  status: string;
  correction_delta: number;
}

export interface CorrectionsResult {
  status: string;
  correction_delta: number;
  calibration_changed: boolean;
  curve_count: number;
}

export interface AvalAIModel {
  id: string;
  owned_by: string;
  mode: string;
  min_tier: number;
  pricing?: {
    input?: number;
    cached_input?: number;
    output?: number;
  };
  max_input_tokens?: number;
  max_output_tokens?: number;
  supports_vision?: boolean;
  supports_function_calling?: boolean;
  supports_pdf_input?: boolean;
  supports_prompt_caching?: boolean;
}

// ── Typed fetch wrapper ────────────────────────────────────────────────────

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...authHeader(), ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 401) {
    localStorage.removeItem("rdp_token");
    localStorage.removeItem("rdp_user");
    window.location.reload();
    throw new Error("Session expired");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Jobs ──────────────────────────────────────────────────────────────────


/** What point finding stored for one crop. */
export interface BatchOcr {
  status: "none" | "running" | "done" | "failed";
  x_ticks?: number[];
  y_ticks?: number[];
  line_labels?: number[];
  x_positions?: number[];
  y_positions?: number[];
  // Parallel arrays holding the OTHER coordinate of each anchor click so
  // the visual marker lands exactly where the operator clicked instead of
  // snapping to the image edge.
  x_positions_py?: number[];
  y_positions_px?: number[];
  line_positions?: [number, number][];
  // Tracer rule-set name (mesh_chart, wind, airport_oat, ...).  Selecting
  // the right one is what makes the tracer separate carpet families.
  category?: string;
  image_width?: number;
  image_height?: number;
  confidence?: number;
  corrected?: boolean;
  corrected_at?: string;
  error?: string;
  // Curve orientation split (family charts): horizontal curves traverse the
  // X-axis, vertical curves traverse the Y-axis.  Missing → fall back to
  // treating all `line_labels` as horizontal.
  h_labels?: number[];
  v_labels?: number[];
}

export interface CropCalibration {
  x_lo: number;
  x_hi: number;
  y_lo: number;
  y_hi: number;
  x_step: number | null;
  y_step: number | null;
  skew_deg: number;
}

export interface CropTrace {
  status: "none" | "running" | "done" | "failed";
  filename?: string;
  curve_count?: number;
  confidence?: number;
  calibration?: CropCalibration;
  /** False when the generic rules were used — nobody has checked them here. */
  tuned?: boolean;
  rules?: string;
  /** How the curve names were arrived at, or that they are only positions. */
  named_by?: string;
  /** Plain-language things the calibration was unsure about. */
  notes?: string[];
  /** The number-reading step is not built yet; always false for now. */
  numbers_read?: boolean;
  error?: string;
}

export interface CropReview {
  job_id: string;
  chart_id: string;
  hitl_init_url: string;
  confidence: number;
  curve_count: number;
  tuned: boolean;
}

/** One handler's slot in the all-tracers manifest returned by the batch runner. */
export interface AllTracersHandler {
  name: string;
  i: number;
  status: "ok" | "error";
  overlay_key?: string;
  /** Presigned MinIO URL — added by the GET endpoint, valid for one hour. */
  overlay_url?: string;
  trace_key?: string;
  curve_count?: number;
  families_count?: number;
  notes?: string[];
  error?: string;
}

/** Live status of a `task_run_all_tracers` run for one crop. */
export interface AllTracersManifest {
  status: "none" | "queued" | "running" | "done" | "failed";
  path?: string;
  filename?: string;
  job_id?: string | null;
  queued_at?: string;
  started_at?: string;
  completed_at?: string;
  handler_count?: number;
  handlers: AllTracersHandler[];
  error?: string;
}

export const api = {
  jobs: {
    list: (status?: JobStatus) =>
      request<JobListItem[]>(`/jobs/${status ? `?status=${status}` : ""}`),

    get: (jobId: string) =>
      request<JobOut>(`/jobs/${jobId}`),

    charts: (jobId: string) =>
      request<ChartOut[]>(`/jobs/${jobId}/charts`),

    pages: (jobId: string) =>
      request<JobPageOut[]>(`/jobs/${jobId}/pages`),

    togglePage: (jobId: string, pageNum: number) =>
      request<JobPageOut>(`/jobs/${jobId}/pages/${pageNum}`, { method: "PATCH" }),

    categorize: (jobId: string) =>
      request<{ status: string; tree: Record<string, Record<string, number[]>>; chart_count: number; batch_folders: string[] }>(
        `/jobs/${jobId}/categorize`, { method: "POST" }
      ),

    pageThumbnailUrl: (jobId: string, pageNum: number) => {
      const token = localStorage.getItem("rdp_token") ?? "";
      return `${BASE}/jobs/${jobId}/pages/${pageNum}/thumbnail?token=${encodeURIComponent(token)}`;
    },

    delete: (jobId: string) =>
      fetch(`${BASE}/jobs/${jobId}`, { method: "DELETE", headers: authHeader() })
        .then((res) => { if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`); }),

    upload: async (file: File, onProgress?: (pct: number) => void): Promise<JobOut> => {
      const form = new FormData();
      form.append("file", file);
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${BASE}/jobs/upload`);
        const token = localStorage.getItem("rdp_token");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status === 201) resolve(JSON.parse(xhr.responseText));
          else reject(new Error(JSON.parse(xhr.responseText)?.detail ?? `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(form);
      });
    },
  },

  models: {
    list: () => request<ModelConfig[]>(`/models`),
    upsert: (stepName: string, body: ModelConfigIn) =>
      request<ModelConfig>(`/models/${stepName}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    errorHistory: (limit = 200) =>
      request<HITLError[]>(`/models/error-history?limit=${limit}`),
    avalaiCredit: () =>
      request<{ credit: Record<string, unknown>; usage_summary: Record<string, unknown> }>(
        `/models/avalai/credit`
      ),
    avalaiModels: () =>
      request<{ object: string; data: AvalAIModel[] }>(`/models/avalai/models`),
  },

  intakeAi: {
    pause: (jobId: string) =>
      request<{ status: string }>(`/jobs/${jobId}/pause`, { method: "POST" }),
    resume: (jobId: string) =>
      request<{ status: string }>(`/jobs/${jobId}/resume`, { method: "POST" }),
    getProgress: (jobId: string) =>
      request<{ pages_done: number; last_page: number; charts_found: number; total: number }>(
        `/jobs/${jobId}/intake-progress`
      ),
  },

  hitl: {
    init: (jobId: string, chartId: string) =>
      request<HITLInitResponse>(`/jobs/${jobId}/charts/${chartId}/hitl-init`),

    approve: (jobId: string, chartId: string, operator?: string) =>
      request<ApproveResult>(`/jobs/${jobId}/charts/${chartId}/approve`, {
        method: "POST",
        body: JSON.stringify({ operator: operator ?? null }),
      }),

    flag: (jobId: string, chartId: string, reason: string) =>
      request<{ status: string }>(
        `/jobs/${jobId}/charts/${chartId}/flag?reason=${encodeURIComponent(reason)}`,
        { method: "POST" }
      ),
  },

  batch: {
    listFolders: (parent?: string) =>
      request<{ parent: string | null; folders: BatchFolder[] }>(
        `/batch/folders${parent ? `?parent=${encodeURIComponent(parent)}` : ""}`
      ),

    createFolder: (name: string, parent?: string) =>
      request<BatchFolder>(`/batch/folders`, {
        method: "POST",
        body: JSON.stringify({ name, parent: parent ?? null }),
      }),

    /** mkdir -p — creates every missing level. Safe to call repeatedly. */
    ensureFolder: (path: string) =>
      request<{ path: string; created: string[] }>(`/batch/folders/ensure`, {
        method: "POST",
        body: JSON.stringify({ path }),
      }),

    deleteFolder: (path: string) =>
      request<{ deleted: boolean }>(`/batch/folders/${encodePath(path)}`, {
        method: "DELETE",
      }),

    contents: (path: string) =>
      request<BatchContents>(`/batch/folders/${encodePath(path)}/contents`),

    extractToFolder: (path: string, jobId: string) =>
      request<{ folder: string; path: string; panels_copied: number }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/extract`,
        { method: "POST" }
      ),

    deleteJob: (path: string, jobId: string) =>
      request<{ deleted: boolean }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}`,
        { method: "DELETE" }
      ),


    /** Queue point finding for one categorised crop sitting in a folder. */
    traceCrop: (path: string, filename: string) =>
      request<{ status: string; filename: string }>(
        `/batch/folders/${encodePath(path)}/trace/${encodeURIComponent(filename)}`,
        { method: "POST" }
      ),

    /** The stored result for one crop. `status: "none"` means never traced. */
    traceStatus: (path: string, filename: string) =>
      request<CropTrace>(
        `/batch/folders/${encodePath(path)}/trace/${encodeURIComponent(filename)}`
      ),

    /** Same, for a panel still sitting inside a PDF's job folder. */
    tracePanel: (path: string, jobId: string, filename: string) =>
      request<{ status: string; filename: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/trace/${encodeURIComponent(filename)}`,
        { method: "POST" }
      ),

    tracePanelStatus: (path: string, jobId: string, filename: string) =>
      request<CropTrace>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/trace/${encodeURIComponent(filename)}`
      ),

    /**
     * Promote a traced crop into a Chart and get the WPD hand-off.
     * Everything after this — the editor, approval, and writing the operator's
     * correction into recipe_memory/rag_store — is the existing HITL chain.
     */
    reviewCrop: (path: string, filename: string, jobId?: string) =>
      request<CropReview>(
        `/batch/folders/${encodePath(path)}/review/${encodeURIComponent(filename)}`,
        { method: "POST", body: JSON.stringify({ job_id: jobId ?? null }) }
      ),

    /** Drop the stored trace for one crop so it can be re-run cleanly. */
    deleteTrace: (path: string, filename: string) =>
      request<{ deleted: boolean }>(
        `/batch/folders/${encodePath(path)}/trace/${encodeURIComponent(filename)}`,
        { method: "DELETE" }
      ),

    deletePanelTrace: (path: string, jobId: string, filename: string) =>
      request<{ deleted: boolean }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/trace/${encodeURIComponent(filename)}`,
        { method: "DELETE" }
      ),

    deletePanel: (path: string, jobId: string, filename: string) =>
      request<{ deleted: boolean }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/file/${encodeURIComponent(filename)}`,
        { method: "DELETE" }
      ),

    deleteImage: (path: string, filename: string) =>
      request<{ deleted: boolean }>(
        `/batch/folders/${encodePath(path)}/images/${encodeURIComponent(filename)}`,
        { method: "DELETE" }
      ),

    sourcePageUrl: (path: string, jobId: string, pageNum: number) =>
      `${BASE}/batch/folders/${encodePath(path)}/jobs/${jobId}/page/${pageNum}`,

    replacePanel: (path: string, jobId: string, filename: string, bbox: number[], pageNum: number) =>
      request<{ status: string; filename: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/replace`,
        { method: "POST", body: JSON.stringify({ bbox, page_num: pageNum }) }
      ),

    queueOcr: (path: string, jobId: string, filename: string) =>
      request<{ status: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/ocr/${encodeURIComponent(filename)}`,
        { method: "POST" }
      ),

    getOcr: (path: string, jobId: string, filename: string) =>
      request<BatchOcr>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/ocr/${encodeURIComponent(filename)}`
      ),

    // WPD 4.7 project tar bundle for a single reviewed panel — packages the
    // crop PNG together with the wizard-set calibration + curve names so the
    // operator can hand-trace curves in WPD without re-picking anchors.
    wpdTarUrl: (path: string, jobId: string, filename: string) =>
      `${BASE}/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/wpd_tar`,

    // Persisted export snapshots so an operator can keep multiple WPD
    // projects per crop (e.g. one per curve family variation).
    saveExport: (path: string, jobId: string, filename: string, name: string) =>
      request<{ name: string; size: number; saved_at: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports`,
        { method: "POST", body: JSON.stringify({ name }) },
      ),
    listExports: (path: string, jobId: string, filename: string) =>
      request<{ exports: {
        name: string; size: number; saved_at: string | null;
        category: string | null;
        effective_category: string | null;
        category_source: "manual" | "folder" | "metadata" | "none";
        uploaded: boolean;
        source_filename: string | null;
        tar_url: string;
        json_url: string;
      }[] }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports`,
      ),
    /** Upload a .wpd.tar or .wpd.json the operator saved out of WPD as the final answer. */
    uploadExport: async (path: string, jobId: string, filename: string,
                        file: File): Promise<{name: string; uploaded_bytes: number; saved_at: string}> => {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("rdp_token") ?? "";
      const res = await fetch(
        `${BASE}/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/upload`,
        {
          method: "POST",
          headers: token ? { "Authorization": `Bearer ${token}` } : {},
          body: form,
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      return res.json();
    },
    exportJsonUrl: (path: string, jobId: string, filename: string, name: string) =>
      `${BASE}/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}.json`,
    /** Promote a saved export into a Chart + HITL session and get the WPD hand-off. */
    openExportInWpd: (path: string, jobId: string, filename: string, name: string) =>
      request<CropReview>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}/open`,
        { method: "POST" },
      ),
    patchExportCategory: (path: string, jobId: string, filename: string,
                          name: string, category: string) =>
      request<{ status: string; category: string | null }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}`,
        { method: "PATCH", body: JSON.stringify({ category }) },
      ),
    exportUrl: (path: string, jobId: string, filename: string, name: string) =>
      `${BASE}/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}`,
    // Bring a saved export back into the modal for editing.  Returns the OCR
    // JSON snapshot; caller replays it into the current OCR to restore the
    // full calibration + curve names.
    loadExportOcr: (path: string, jobId: string, filename: string, name: string) =>
      request<BatchOcr>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}/ocr`,
      ),

    // Trigger a trace using the export's saved calibration.  The worker
    // rewrites the export tar so subsequent downloads include the points.
    traceExport: (path: string, jobId: string, filename: string, name: string) =>
      request<{ status: string; export_name: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}/trace`,
        { method: "POST" },
      ),
    getExportTrace: (path: string, jobId: string, filename: string, name: string) =>
      request<{
        status: "none" | "running" | "done" | "failed";
        curve_count?: number;
        confidence?: number;
        error?: string;
        finished_at?: string;
      }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}/trace`,
      ),
    deleteExport: (path: string, jobId: string, filename: string, name: string) =>
      request<{ status: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/exports/${encodeURIComponent(name)}`,
        { method: "DELETE" },
      ),

    /** Fire the batch runner: every registered chart-tracing handler on this crop. */
    startAllTracers: (path: string, filename: string, jobId?: string) =>
      request<{ status: string; task_id: string; filename: string }>(
        `/batch/folders/${encodePath(path)}/all-tracers/${encodeURIComponent(filename)}`,
        { method: "POST", body: JSON.stringify({ job_id: jobId ?? null }) },
      ),

    /** Live manifest of the run (poll every ~1.5 s while status ≠ "done"). */
    getAllTracers: (path: string, filename: string, jobId?: string) => {
      const q = jobId ? `?job_id=${encodeURIComponent(jobId)}` : "";
      return request<AllTracersManifest>(
        `/batch/folders/${encodePath(path)}/all-tracers/${encodeURIComponent(filename)}${q}`,
      );
    },

    /** Promote the picked handler's trace and hand off to the same WPD flow as reviewCrop. */
    selectAllTracersHandler: (path: string, filename: string,
                              handlerName: string, jobId?: string) =>
      request<CropReview>(
        `/batch/folders/${encodePath(path)}/all-tracers/${encodeURIComponent(filename)}/select`,
        {
          method: "POST",
          body: JSON.stringify({ handler_name: handlerName, job_id: jobId ?? null }),
        },
      ),

    // Effective category the tracer WOULD use for this panel now
    // (manual dropdown → folder / filename keyword → metadata → none).
    // Used by the tile to show what Find Points is about to do.
    getPanelEffectiveCategory: (path: string, jobId: string, filename: string) =>
      request<{ category: string | null; source: "manual" | "folder" | "metadata" | "none" }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/effective-category`,
      ),
    patchPanelCategory: (path: string, jobId: string, filename: string, category: string) =>
      request<{ category: string | null; source: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/category`,
        { method: "PATCH", body: JSON.stringify({ category }) },
      ),

    // One-click LLM analyze: sends the crop to the configured
    // chart_analyze model and applies the suggested name + category +
    // axis / curve hints to the panel's OCR JSON.  Returns tokens + cost.
    aiAnalyzePanel: (path: string, jobId: string, filename: string) =>
      request<{
        name: string | null;
        category: string | null;
        x_axis_label: string | null;
        y_axis_label: string | null;
        x_min: number | null;
        x_max: number | null;
        y_min: number | null;
        y_max: number | null;
        h_curve_labels: number[];
        v_curve_labels: number[];
        model_name: string | null;
        input_tokens: number;
        output_tokens: number;
        cost_usd: number;
        rename_applied: boolean;
      }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/panels/${encodeURIComponent(filename)}/ai-analyze`,
        { method: "POST" },
      ),

    // Rename the source PDF stored in a job's meta.json.  Only the display
    // name changes; S3 keys stay where they are, so no bytes move.
    renameJob: (path: string, jobId: string, filename: string) =>
      request<{ status: string; filename: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}`,
        { method: "PATCH", body: JSON.stringify({ filename }) },
      ),

    correctOcr: (
      path: string, jobId: string, filename: string,
      xTicks: number[], yTicks: number[], lineLabels: number[],
      opts?: {
        xPositions?: number[];
        yPositions?: number[];
        xPositionsPy?: number[];
        yPositionsPx?: number[];
        linePositions?: [number, number][];
        hLabels?: number[];
        vLabels?: number[];
        category?: string;
      },
    ) =>
      request<{ status: string }>(
        `/batch/folders/${encodePath(path)}/jobs/${jobId}/ocr/${encodeURIComponent(filename)}`,
        {
          method: "PUT",
          body: JSON.stringify({
            x_ticks: xTicks,
            y_ticks: yTicks,
            line_labels: lineLabels,
            x_positions: opts?.xPositions,
            y_positions: opts?.yPositions,
            x_positions_py: opts?.xPositionsPy,
            y_positions_px: opts?.yPositionsPx,
            line_positions: opts?.linePositions,
            h_labels: opts?.hLabels,
            v_labels: opts?.vLabels,
            category: opts?.category,
          }),
        },
      ),

    downloadUrl: (path: string) =>
      `${BASE}/batch/folders/${encodePath(path)}/download`,

    categorize: (path: string, dryRun: boolean) =>
      request<{
        path: string;
        dry_run: boolean;
        folders_created?: number;
        charts_copied?: number;
        panels_without_a_name?: number;
        groups: { name: string; panel_count: number; pdf_count: number; named: number }[];
      }>(`/batch/folders/${encodePath(path)}/categorize?dry_run=${dryRun}`, {
        method: "POST",
      }),

    redetect: (path: string) =>
      request<{ path: string; queued: number }>(
        `/batch/folders/${encodePath(path)}/redetect`,
        { method: "POST" }
      ),

    datasetUrl: (path: string) =>
      `${BASE}/batch/folders/${encodePath(path)}/dataset`,

    cropPDF: async (path: string, file: File, onProgress?: (pct: number) => void) => {
      const form = new FormData();
      form.append("file", file);
      return new Promise<BatchCropResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${BASE}/batch/folders/${encodePath(path)}/crop`);
        const token = localStorage.getItem("rdp_token");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          // 202: stored and queued for a worker.
          if (xhr.status === 200 || xhr.status === 202) resolve(JSON.parse(xhr.responseText));
          else {
            let detail = `HTTP ${xhr.status}`;
            try {
              detail = JSON.parse(xhr.responseText)?.detail ?? detail;
            } catch {
              // Non-JSON error body (proxy error page) — keep the status code.
            }
            reject(new Error(detail));
          }
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(form);
      });
    },
  },
};

// ── WebSocket hook helper (used in stores) ────────────────────────────────

export function openJobSocket(
  jobId: string,
  onMessage: (data: Record<string, unknown>) => void
): () => void {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${proto}://${window.location.host}/ws/jobs/${jobId}/status`);

  ws.onmessage = (e) => {
    try {
      onMessage(JSON.parse(e.data));
    } catch {
      // ignore malformed frames
    }
  };

  // Keepalive ping every 25 s
  const ping = setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) ws.send("ping");
  }, 25_000);

  return () => {
    clearInterval(ping);
    ws.close();
  };
}
